// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { zodResolver } from "@hookform/resolvers/zod";
import { TriangleAlert } from "lucide-react";
import { useEffect, useId, useImperativeHandle, useMemo, useRef } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { z } from "zod";
import { useEditStoreMutation, useStoreDetailQuery } from "@/api/hooks/use-stores";
import { PageFooter } from "@/components/layout/page-footer";
import { DatePicker } from "@/components/shared/date-picker";
import { SaveDiscardPane } from "@/components/shared/save-discard-pane";
import { VirtualSelectPopover } from "@/components/shared/virtual-select-popover";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { isGoLiveBeforeInstall, STORE_DATE_FROM_YEAR, storeDateToMonth } from "@/features/stores/store-date-bounds";
import { useBackofficeContext } from "@/hooks/use-backoffice-context";
import { COUNTRY_OPTIONS } from "@/lib/countries";
import { countDirtyFields } from "@/lib/dirty-fields";
import { cn } from "@/lib/utils";

const storeInfoSchema = z.object({
  storeCode: z.string().min(1, "Required"),
  legibleName: z.string().min(1, "Required").max(100),
  // null is the empty value (matches the API) so clearing the date sends null and isn't left dirty
  goLiveDate: z.string().nullish(),
  installDate: z.string().nullish(),
  isActive: z.boolean(),

  // Address — nullish so the null values the API returns (unknown address) pass validation
  address: z.object({
    country: z.string().nullish(),
    region: z.string().nullish(),
    division: z.string().nullish(),
    city: z.string().nullish(),
    state: z.string().nullish(),
    zipCode: z.string().nullish(),
    address: z.string().nullish(),
  }),
});

type StoreInfoValues = z.infer<typeof storeInfoSchema>;

/** The form's schema plus the cross-field date rule, whose message has to be translated. */
function makeStoreInfoSchema(goLiveBeforeInstallMessage: string) {
  return storeInfoSchema.superRefine((values, ctx) => {
    if (isGoLiveBeforeInstall(values.installDate, values.goLiveDate)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: goLiveBeforeInstallMessage, path: ["goLiveDate"] });
    }
  });
}

export interface StoreEditTabHandle {
  reset: () => void;
}

interface StoreEditTabProps {
  storeName: string;
  onDirtyChange?: (dirty: boolean) => void;
  ref?: React.Ref<StoreEditTabHandle>;
}

export function StoreEditTab({ storeName, onDirtyChange, ref }: StoreEditTabProps) {
  const { t } = useTranslation("back-office");
  const { data: storeRaw } = useStoreDetailQuery(storeName);
  const editStore = useEditStoreMutation(storeName);

  // Cast the raw API response
  const { data: context } = useBackofficeContext();

  const store = storeRaw as unknown as StoreInfoValues | undefined;
  // The address has no values set yet (e.g. a freshly created / empty store)
  const isMissingAddress = !!storeRaw && !Object.values(storeRaw.address ?? {}).some(Boolean);
  // Regions available for this store's chain
  const regionOptions = (context?.regionsByChain[storeRaw?.chainName ?? ""] ?? []).map((r) => ({
    label: r,
    value: r,
  }));
  // Divisions available for this store's chain
  const divisionOptions = (context?.divisionsByChain[storeRaw?.chainName ?? ""] ?? []).map((d) => ({
    label: d,
    value: d,
  }));

  const emptyDefaults: StoreInfoValues = {
    storeCode: "",
    legibleName: "",
    goLiveDate: null,
    installDate: null,
    isActive: false,
    address: {},
  };

  const schema = useMemo(() => makeStoreInfoSchema(t("stores.form.goLiveBeforeInstall")), [t]);
  const formId = useId();
  const form = useForm<StoreInfoValues>({
    resolver: zodResolver(schema),
    defaultValues: store ?? emptyDefaults,
  });
  // The date-order rule sits on Go-Live Date; re-check it as soon as either date moves, so the
  // message appears (and clears) while editing rather than only on save. Nothing is blocked.
  function onDateChange(onChange: (value: string | null) => void) {
    return (value: string | undefined) => {
      onChange(value ?? null);
      void form.trigger("goLiveDate");
    };
  }

  useEffect(
    function resetFormWhenStoreDataLoads() {
      if (store) {
        form.reset(store);
      }
    },
    [store, form],
  );

  async function onSubmit(values: StoreInfoValues) {
    try {
      await editStore.mutateAsync({
        info: {
          storeCode: values.storeCode,
          legibleName: values.legibleName,
          // send explicit null to clear the date; API accepts null even though the generated type is string | undefined
          goLiveDate: (values.goLiveDate ?? null) as string | undefined,
          installDate: (values.installDate ?? null) as string | undefined,
          isActive: values.isActive,
          // The API returns unknown address fields as null; convert to undefined so they're omitted
          address: {
            country: values.address.country ?? undefined,
            region: values.address.region ?? undefined,
            division: values.address.division ?? undefined,
            city: values.address.city ?? undefined,
            state: values.address.state ?? undefined,
            zipCode: values.address.zipCode ?? undefined,
            address: values.address.address ?? undefined,
          },
        },
      });
      toast.success(t("stores.notifications.updated", { name: values.legibleName }));
      // Reset to the form's own current values so dirty state clears on 200 (independent of refetch)
      form.reset(form.getValues());
    } catch {
      // Error toast handled by API middleware
    }
  }

  const { isDirty, dirtyFields } = form.formState;

  useImperativeHandle(ref, () => ({ reset: () => form.reset() }));

  const onDirtyChangeRef = useRef(onDirtyChange);
  onDirtyChangeRef.current = onDirtyChange;
  useEffect(
    function notifyParentOfDirtyState() {
      onDirtyChangeRef.current?.(isDirty);
    },
    [isDirty],
  );

  return (
    <div className="max-w-2xl space-y-6 p-6">
      <Form {...form}>
        <form id={formId} onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <FormField
            control={form.control}
            name="legibleName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("stores.form.legibleName")}</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    className={cn(dirtyFields.legibleName && "bg-sidebar-accent-hover transition-colors")}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="storeCode"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("stores.form.storeCode")}</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    className={cn("font-mono", dirtyFields.storeCode && "bg-sidebar-accent-hover transition-colors")}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          {/* `items-start`: a validation message under one date mustn't stretch the other's cell. */}
          <div className="grid grid-cols-2 items-start gap-4">
            <FormField
              control={form.control}
              name="installDate"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("stores.form.installDate")}</FormLabel>
                  <FormControl>
                    <DatePicker
                      value={field.value ?? undefined}
                      onChange={onDateChange(field.onChange)}
                      placeholder={t("stores.form.notSet")}
                      fromYear={STORE_DATE_FROM_YEAR}
                      toMonth={storeDateToMonth()}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="goLiveDate"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("stores.form.goLiveDate")}</FormLabel>
                  <FormControl>
                    <DatePicker
                      value={field.value ?? undefined}
                      onChange={onDateChange(field.onChange)}
                      placeholder={t("stores.form.notSet")}
                      fromYear={STORE_DATE_FROM_YEAR}
                      toMonth={storeDateToMonth()}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <Separator />

          {isMissingAddress && (
            <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-200">
              <TriangleAlert className="h-4 w-4 shrink-0 mt-0.5" />
              <span>{t("stores.missingDataWarning")}</span>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="address.address"
              render={({ field }) => (
                <FormItem className="col-span-2">
                  <FormLabel>{t("stores.form.address")}</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      value={field.value ?? ""}
                      autoComplete="off"
                      data-1p-ignore
                      data-lpignore="true"
                      className={cn(dirtyFields.address?.address && "bg-sidebar-accent-hover transition-colors")}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="address.city"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("stores.form.city")}</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      value={field.value ?? ""}
                      autoComplete="off"
                      data-1p-ignore
                      data-lpignore="true"
                      className={cn(dirtyFields.address?.city && "bg-sidebar-accent-hover transition-colors")}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="address.zipCode"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("stores.form.zipCode")}</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      value={field.value ?? ""}
                      autoComplete="off"
                      data-1p-ignore
                      data-lpignore="true"
                      className={cn(dirtyFields.address?.zipCode && "bg-sidebar-accent-hover transition-colors")}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="address.state"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("stores.form.state")}</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      value={field.value ?? ""}
                      autoComplete="off"
                      data-1p-ignore
                      data-lpignore="true"
                      className={cn(dirtyFields.address?.state && "bg-sidebar-accent-hover transition-colors")}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="address.country"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("stores.form.country")}</FormLabel>
                  <FormControl>
                    <VirtualSelectPopover
                      value={field.value ?? ""}
                      onChange={field.onChange}
                      options={COUNTRY_OPTIONS}
                      placeholder={t("stores.form.selectCountry")}
                      searchPlaceholder={t("stores.form.searchCountries")}
                      emptyText={t("stores.form.noCountries")}
                      className={cn(dirtyFields.address?.country && "bg-sidebar-accent-hover transition-colors")}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="address.region"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("stores.form.region")}</FormLabel>
                  <FormControl>
                    <VirtualSelectPopover
                      value={field.value ?? ""}
                      onChange={field.onChange}
                      options={regionOptions}
                      allowCustom
                      placeholder={t("stores.form.selectRegion")}
                      searchPlaceholder={t("stores.form.searchRegions")}
                      emptyText={t("stores.form.noRegions")}
                      className={cn(dirtyFields.address?.region && "bg-sidebar-accent-hover transition-colors")}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="address.division"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("stores.form.division")}</FormLabel>
                  <FormControl>
                    <VirtualSelectPopover
                      value={field.value ?? ""}
                      onChange={field.onChange}
                      options={divisionOptions}
                      allowCustom
                      placeholder={t("stores.form.selectDivision")}
                      searchPlaceholder={t("stores.form.searchDivisions")}
                      emptyText={t("stores.form.noDivisions")}
                      className={cn(dirtyFields.address?.division && "bg-sidebar-accent-hover transition-colors")}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>

          {isDirty && (
            <PageFooter>
              <SaveDiscardPane
                form={formId}
                docked
                onDiscard={() => form.reset()}
                saving={editStore.isPending}
                changeCount={countDirtyFields(dirtyFields)}
              />
            </PageFooter>
          )}
        </form>
      </Form>
    </div>
  );
}
